import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import rankingService from "../../services/rankingService";
import jobOfferService from "../../services/jobOfferService";
import applicationService from "../../services/applicationService";
import { getErrorMessage } from "../../utils/errorHandler";
import PageHeader from "../../components/ui/PageHeader";
import FilterBar from "../../components/ui/FilterBar";
import { Play, Eye, RefreshCw, TrendingUp, Award, CheckCircle2, AlertTriangle, X, FileText } from "lucide-react";
import SkillBadges from "../../components/common/SkillBadges";
import "../../styles/recruitment.css";
import "../../styles/dashboard.css";

const parseJsonList = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const CVRanking = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [jobOffers, setJobOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState("");
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fonction pour générer une recommandation dynamique selon le score
  const generateRecommendation = (score, matchedSkills, missingSkills) => {
    const matchedSkillsList = parseJsonList(matchedSkills);
    const missingSkillsList = parseJsonList(missingSkills);

    if (score >= 80) {
      const topSkills = matchedSkillsList.slice(0, 3).join(", ");
      return `Très bon niveau de correspondance avec l'offre. Le candidat couvre la majorité des compétences recherchées, notamment ${topSkills}. Les quelques compétences manquantes peuvent èªtre vérifiées pendant l'entretien.`;
    } else if (score >= 65) {
      const topSkills = matchedSkillsList.slice(0, 2).join(", ");
      return `Profil recommandé avec une bonne correspondance. Le candidat possède plusieurs compétences clés comme ${topSkills}. Certaines compétences peuvent èªtre validées pendant l'entretien.`;
    } else if (score >= 50) {
      return `Profil intéressant mais partiellement aligné avec l'offre. Plusieurs compétences importantes sont présentes, mais certaines exigences techniques doivent èªtre validées pendant l'entretien.`;
    } else if (score >= 30) {
      return `Profil partiellement adapté à cette offre. Le candidat possède quelques compétences pertinentes, mais plusieurs écarts importants avec les exigences de l'offre doivent èªtre discutés.`;
    } else {
      return `Le profil présente actuellement un faible niveau de correspondance avec cette offre. Plusieurs compétences essentielles ne sont pas identifiées dans le CV. Le candidat est peu recommandé pour cette offre en l'état.`;
    }
  };

  // Fonction pour obtenir la couleur selon le score
  const getScoreColor = (score) => {
    if (score >= 80) return "#059669"; // vert
    if (score >= 65) return "#0284c7"; // bleu positif
    if (score >= 50) return "#d97706"; // orange
    if (score >= 30) return "#ea580c"; // orange foncé
    return "#dc2626"; // rouge
  };

  // Support touche Escape pour fermer la modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && showDetails) {
        setShowDetails(null);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [showDetails]);

  useEffect(() => {
    loadJobOffers();
  }, []);

  const loadJobOffers = async () => {
    try {
      setLoading(true);
      const response = await jobOfferService.getAll();
      const offersList = Array.isArray(response) 
        ? response 
        : (response?.data || []);
      setJobOffers(offersList);
      if (offersList.length > 0) {
        setSelectedOffer(String(offersList[0].id));
      }
    } catch (err) {
      console.error("Error loading job offers:", err);
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const analyzeRanking = async () => {
    if (!selectedOffer) {
      toast.error("Veuillez sélectionner une offre d'emploi");
      return;
    }

    try {
      setAnalyzing(true);
      const results = await rankingService.getRanking(selectedOffer);
      setRankings(results || []);
      toast.success(`Analyse IA terminée : ${(results || []).length} candidat(s) évalué(s).`);
    } catch (err) {
      console.error("Error analyzing ranking:", err);
      toast.error(getErrorMessage(err));
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    if (selectedOffer) {
      analyzeRanking();
    }
  }, [selectedOffer]);

  const filteredRankings = rankings.filter(r => {
    const candidateName = (r.candidate_name || "").toLowerCase();
    const q = searchTerm.toLowerCase();
    return candidateName.includes(q);
  });

  return (
    <div style={{ padding: "24px" }}>
      <PageHeader
        title="Ranking Détaillé par IA"
        subtitle="Classez automatiquement les candidatures grè¢ce au moteur RAG et à la correspondance sémantique des CV."
        actions={
          <button
            onClick={analyzeRanking}
            disabled={analyzing}
            className="btn btn-primary"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <Play size={16} />
            <span>{analyzing ? "Analyse IA en cours..." : "Lancer le classement IA"}</span>
          </button>
        }
      />

      {/* Offer Select & Filter Bar */}
      <div className="ui-card" style={{ padding: "16px", marginBottom: "20px", display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: "1 1 300px" }}>
          <span style={{ fontSize: "0.88rem", fontWeight: "600", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
            Offre d'emploi :
          </span>
          <select
            value={selectedOffer}
            onChange={(e) => setSelectedOffer(e.target.value)}
            style={{
              width: "100%",
              padding: "9px 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border)",
              backgroundColor: "var(--bg-main)",
              fontSize: "0.9rem",
              fontWeight: "600",
              color: "var(--text-main)"
            }}
          >
            {jobOffers.map((offer) => (
              <option key={offer.id} value={String(offer.id)}>
                {offer.title} ({offer.location || "Télétravail"})
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1 1 200px" }}>
          <input
            type="text"
            placeholder="Filtrer par nom du candidat..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "9px 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border)",
              backgroundColor: "var(--bg-main)",
              fontSize: "0.9rem"
            }}
          />
        </div>
      </div>

      {/* Ranking Results */}
      {analyzing ? (
        <div className="ui-card" style={{ padding: "60px", textAlign: "center", color: "var(--text-muted)" }}>
          <div className="calendar-loading-spinner" style={{ width: "40px", height: "40px", border: "3px solid var(--border)", borderTopColor: "var(--primary)", borderRadius: "50%", margin: "0 auto 16px" }} />
          Analyse sémantique des CV et évaluation par IA en cours...
        </div>
      ) : filteredRankings.length === 0 ? (
        <div className="ui-card" style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
          Aucun classement disponible pour cette offre. Cliquez sur "Lancer le classement IA".
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {filteredRankings.map((rank, idx) => {
            const score = rank.score || rank.matching_score || 0;
            const matchedSkills = parseJsonList(rank.matched_skills);
            const missingSkills = parseJsonList(rank.missing_skills);
            const rankNumber = idx + 1;

            return (
              <div
                key={rank.id || idx}
                className="ui-card ui-card-interactive"
                style={{
                  padding: "20px",
                  display: "flex",
                  gap: "20px",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap"
                }}
              >
                {/* Rank Badge & Candidate Info */}
                <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: "1 1 280px" }}>
                  <div style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    backgroundColor: rankNumber === 1 ? "#fef3c7" : rankNumber === 2 ? "#e0e7ff" : rankNumber === 3 ? "#fee2e2" : "var(--bg-subtle)",
                    color: rankNumber === 1 ? "#b45309" : rankNumber === 2 ? "#3730a3" : rankNumber === 3 ? "#b91c1c" : "var(--text-muted)",
                    fontWeight: "800",
                    fontSize: "1.1rem",
                    display: "grid",
                    placeItems: "center"
                  }}>
                    #{rankNumber}
                  </div>

                  <div>
                    <h4 style={{ fontSize: "1.05rem", fontWeight: "700", color: "var(--text-main)", margin: 0 }}>
                      {rank.candidate_name || `Candidat #${rank.candidate_id}`}
                    </h4>
                  </div>
                </div>

                {/* Progress Bar & Score */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: "0 0 180px" }}>
                  <div style={{ flex: 1, height: "10px", backgroundColor: "var(--bg-muted)", borderRadius: "var(--radius-pill)", overflow: "hidden" }}>
                    <div style={{
                      width: `${Math.min(100, Math.max(0, score))}%`,
                      height: "100%",
                      backgroundColor: score >= 75 ? "#059669" : score >= 50 ? "#d97706" : "#dc2626",
                      borderRadius: "var(--radius-pill)"
                    }} />
                  </div>
                  <span style={{ fontSize: "1.1rem", fontWeight: "800", color: "var(--text-main)" }}>
                    {score}%
                  </span>
                </div>

                {/* Skills Summary */}
                <div style={{ flex: "1 1 300px" }}>
                  {matchedSkills.length > 0 && (
                    <div style={{ marginBottom: "4px" }}>
                      <span style={{ fontSize: "0.75rem", fontWeight: "700", color: "#059669", marginRight: "6px" }}>✓ Match:</span>
                      <SkillBadges skills={matchedSkills.slice(0, 3)} variant="matched" />
                    </div>
                  )}
                  {missingSkills.length > 0 && (
                    <div>
                      <span style={{ fontSize: "0.75rem", fontWeight: "700", color: "#dc2626", marginRight: "6px" }}>✗ Manquant:</span>
                      <SkillBadges skills={missingSkills.slice(0, 2)} variant="missing" />
                    </div>
                  )}
                </div>

                {/* Detail Button */}
                <button
                  onClick={() => setShowDetails(rank)}
                  className="btn btn-outline"
                  style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.85rem" }}
                >
                  <Eye size={16} />
                  <span>Détails IA</span>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Details Modal */}
      {showDetails && (
        <div className="modal-overlay" onClick={() => setShowDetails(null)}>
          <div className="modal-form" onClick={(e) => e.stopPropagation()} style={{ padding: "32px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", paddingBottom: "16px", borderBottom: "1px solid var(--border)" }}>
              <h3 style={{ margin: 0, fontSize: "1.3rem", fontWeight: "700", color: "var(--text-main)" }}>
                Analyse détaillée du CV
              </h3>
              <button onClick={() => setShowDetails(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: "8px" }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {/* Candidat et Offre */}
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <label style={{ fontSize: "0.8rem", fontWeight: "600", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Candidat</label>
                  <div style={{ fontSize: "1rem", fontWeight: "600", color: "var(--text-main)", marginTop: "4px" }}>
                    {showDetails.candidate_name || `Candidat #${showDetails.candidate_id}`}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <label style={{ fontSize: "0.8rem", fontWeight: "600", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>Offre</label>
                  <div style={{ fontSize: "1rem", fontWeight: "600", color: "var(--text-main)", marginTop: "4px" }}>
                    {jobOffers.find(o => String(o.id) === selectedOffer)?.title || "Offre sélectionnée"}
                  </div>
                </div>
              </div>

              {/* Score Section */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px", backgroundColor: "var(--primary-light)", borderRadius: "var(--radius-md)", border: "1px solid var(--primary)" }}>
                <span style={{ fontSize: "0.95rem", fontWeight: "600", color: "var(--primary)" }}>Score de correspondance</span>
                <span style={{ fontSize: "1.5rem", fontWeight: "800", color: getScoreColor(showDetails.score || showDetails.matching_score) }}>{(showDetails.score || showDetails.matching_score).toFixed(2)}%</span>
              </div>

              {/* Compétences détectées */}
              <div>
                <h5 style={{ fontSize: "1rem", fontWeight: "700", marginBottom: "12px", color: "var(--text-main)" }}>Compétences détectées</h5>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {parseJsonList(showDetails.matched_skills).length > 0 ? (
                    parseJsonList(showDetails.matched_skills).map((skill, idx) => (
                      <span key={idx} style={{
                        backgroundColor: "#dbeafe",
                        color: "#1e40af",
                        padding: "6px 14px",
                        borderRadius: "20px",
                        fontSize: "0.85rem",
                        fontWeight: "500",
                        border: "1px solid #93c5fd"
                      }}>
                        {skill}
                      </span>
                    ))
                  ) : (
                    <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>Aucune compétence détectée</span>
                  )}
                </div>
              </div>

              {/* Compétences manquantes */}
              <div>
                <h5 style={{ fontSize: "1rem", fontWeight: "700", marginBottom: "12px", color: "var(--text-main)" }}>Compétences manquantes</h5>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {parseJsonList(showDetails.missing_skills).length > 0 ? (
                    parseJsonList(showDetails.missing_skills).map((skill, idx) => (
                      <span key={idx} style={{
                        backgroundColor: "#fee2e2",
                        color: "#dc2626",
                        padding: "6px 14px",
                        borderRadius: "20px",
                        fontSize: "0.85rem",
                        fontWeight: "500",
                        border: "1px solid #fca5a5"
                      }}>
                        {skill}
                      </span>
                    ))
                  ) : (
                    <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>Aucune compétence essentielle manquante détectée</span>
                  )}
                </div>
              </div>

              {/* Points forts / Points à améliorer */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px" }} className="cv-details-grid">
                {/* Points forts */}
                <div style={{
                  backgroundColor: "#d1fae5",
                  border: "1px solid #86efac",
                  borderRadius: "var(--radius-md)",
                  padding: "20px"
                }}>
                  <h5 style={{ fontSize: "1rem", fontWeight: "700", marginBottom: "12px", color: "#065f46", display: "flex", alignItems: "center", gap: "8px" }}>
                    <CheckCircle2 size={18} />
                    Points forts
                  </h5>
                  <ul style={{ margin: 0, paddingLeft: "20px", color: "#065f46", fontSize: "0.9rem", lineHeight: "1.6" }}>
                    {[...new Set(parseJsonList(showDetails.matched_skills))].map((skill, idx) => (
                      <li key={idx}>{skill}</li>
                    ))}
                  </ul>
                </div>

                {/* Points à améliorer */}
                <div style={{
                  backgroundColor: "#fed7aa",
                  border: "1px solid #fdba74",
                  borderRadius: "var(--radius-md)",
                  padding: "20px"
                }}>
                  <h5 style={{ fontSize: "1rem", fontWeight: "700", marginBottom: "12px", color: "#9a3412", display: "flex", alignItems: "center", gap: "8px" }}>
                    <AlertTriangle size={18} />
                    Points à améliorer
                  </h5>
                  <ul style={{ margin: 0, paddingLeft: "20px", color: "#9a3412", fontSize: "0.9rem", lineHeight: "1.6" }}>
                    {[...new Set(parseJsonList(showDetails.missing_skills))].map((skill, idx) => (
                      <li key={idx}>{skill}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Recommandation */}
              <div style={{
                backgroundColor: "#d1fae5",
                border: "1px solid #86efac",
                borderRadius: "var(--radius-md)",
                padding: "20px"
              }}>
                <h5 style={{ fontSize: "1rem", fontWeight: "700", marginBottom: "12px", color: "#065f46", display: "flex", alignItems: "center", gap: "8px" }}>
                  <CheckCircle2 size={18} />
                  Recommandation
                </h5>
                <p style={{ fontSize: "0.95rem", color: "#065f46", lineHeight: "1.6", margin: 0 }}>
                  {generateRecommendation(showDetails.score || showDetails.matching_score, showDetails.matched_skills, showDetails.missing_skills)}
                </p>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "24px", paddingTop: "16px", borderTop: "1px solid var(--border)" }}>
              <button onClick={() => setShowDetails(null)} className="btn btn-primary">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CVRanking;

